import { useCallback } from 'react';
import type { PageContext, StepPageBlock, UITrigger } from '@template-project/funnels-contract';
import { useOptionalFunnelContext } from '../funnel-provider';
import { emitBlockEvent } from './emit-block-event';
import { isFunnelUiEventsEnabled } from './feature-flag';

export function useBlockEventEmitter(
  block: StepPageBlock,
  pageContext: Partial<PageContext> = {},
) {
  const funnel = useOptionalFunnelContext();

  const emitTrigger = useCallback(
    (trigger: UITrigger, value?: unknown, eventMeta?: Record<string, unknown>) => {
      if (!isFunnelUiEventsEnabled() || !funnel) return;
      const bindings = block.events?.[trigger];
      if (!bindings?.length) return;

      for (const binding of bindings) {
        emitBlockEvent({
          bus: funnel.eventBus,
          binding,
          trigger,
          block,
          pageContext,
          sessionId: funnel.sessionId,
          tenantId: funnel.config.tenantId,
          funnelId: funnel.activeExperience?.funnelId,
          stepId: funnel.activeExperience?.stepId,
          variantKey: funnel.experimentVariantKey ?? undefined,
          value,
          eventMeta,
        });
      }
    },
    [block, funnel, pageContext],
  );

  return {
    emitTrigger,
    onClick: () => emitTrigger('onClick'),
    onSubmit: (value?: unknown) => emitTrigger('onSubmit', value),
    onView: () => emitTrigger('onView'),
    onChange: (value?: unknown) => emitTrigger('onChange', value),
    // WP-1: complete the authorable trigger vocabulary so onClose/onHover
    // bindings are never silently dropped. Blocks that expose a close/hover
    // affordance wire these; support matrix lives in the funnel-events model doc.
    onClose: () => emitTrigger('onClose'),
    onHover: () => emitTrigger('onHover'),
    onTimeout: () => emitTrigger('onTimeout'),
  };
}
